import logger from "@/lib/logging/logger";
import {
	addInboundNativeMessageListener,
	type InboundNativeMessage,
	type NativeCameraBarcodePayload,
	type NativeCameraCapturePayload,
	type NativeCameraConfigurePayload,
	type NativeCameraErrorCode,
	type NativeCameraErrorPayload,
	type NativeCameraFacing,
	type NativeCameraFocusEventPayload,
	type NativeCameraFocusPayload,
	type NativeCameraGeometryPayload,
	type NativeCameraPhotoPayload,
	type NativeCameraReadyPayload,
	type NativeCameraSlot,
	type NativeCameraStartPayload,
	type NativeCameraStatePayload,
	type NativeCameraStoppedPayload,
	type NativeCameraTextPayload,
	type NativeReadyPayload,
	type NativeThermalState,
	sendOutboundNativeMessage,
} from "@/lib/native-bridge";

const START_TIMEOUT_MS = 10_000;
const CAPTURE_TIMEOUT_MS = 15_000;
// The wrapper promises a settled focus within 1.5 s.
const FOCUS_SETTLE_TIMEOUT_MS = 2_000;

export type NativeCameraStatus = "idle" | "starting" | "ready" | "interrupted" | "stopped" | "error";

export type NativeCameraFailure = { code: NativeCameraErrorCode | "timeout"; message: string };

export type NativeCameraSnapshot = {
	status: NativeCameraStatus;
	slotActive: boolean;
	facing: NativeCameraFacing | null;
	capabilities: NativeCameraReadyPayload["capabilities"] | null;
	geometry: NativeCameraGeometryPayload | null;
	state: NativeCameraStatePayload | null;
	/** The latest tap-to-focus phase; null until the page asks for a focus point. */
	focus: NativeCameraFocusEventPayload | null;
	/** iOS thermal state from `native.ready` and `device.thermal`; null until the wrapper reports one. */
	thermal: NativeThermalState | null;
	/** Hardware model identifier from `native.ready`/`pong`; null on wrapper builds that predate it. */
	deviceModel: string | null;
	error: NativeCameraFailure | null;
	lastEvent: string | null;
};

export type NativeFocusSupport = "focus" | "exposure" | "none";

/** What a tap on the slot can do with the current camera, per the `camera.ready` capabilities. */
export const nativeFocusSupport = (capabilities: NativeCameraSnapshot["capabilities"]): NativeFocusSupport => {
	if (!capabilities) return "none";
	if (capabilities.focusPoint) return "focus";
	if (capabilities.exposurePoint) return "exposure";
	return "none";
};

export class NativeCameraError extends Error {
	readonly code: NativeCameraErrorCode | "timeout" | "superseded";

	constructor(code: NativeCameraError["code"], message: string) {
		super(message);
		this.name = "NativeCameraError";
		this.code = code;
	}
}

// The shared logger only ships allow-listed error names, so the code travels as plain fields.
export const describeNativeCameraError = (error: unknown): { code: string; detail: string } => ({
	code: error instanceof NativeCameraError ? error.code : "unknown",
	detail: error instanceof Error ? error.message : String(error),
});

type Deferred<T> = { resolve: (value: T) => void; reject: (error: Error) => void; timer: number };

const INITIAL_SNAPSHOT: NativeCameraSnapshot = {
	status: "idle",
	slotActive: false,
	facing: null,
	capabilities: null,
	geometry: null,
	state: null,
	focus: null,
	thermal: null,
	deviceModel: null,
	error: null,
	lastEvent: null,
};

/**
 * The page's own box next to the slot it reports. A slot is in page coordinates, which equal screen
 * points only while the page covers the screen; a page inset by the safe areas shifts the two apart,
 * and the wrapper paints the preview in its own space. Logging both makes that mismatch readable
 * instead of leaving it to be inferred from a misplaced preview.
 */
const pageViewport = () => ({
	width: window.innerWidth,
	height: window.innerHeight,
	screenWidth: window.screen?.width,
	screenHeight: window.screen?.height,
});

// Legitimate slot-echo drift is layout rounding (the wrapper rounds to 2 decimals); a real
// displacement is tens of px of safe-area inset.
const SLOT_ECHO_TOLERANCE_PX = 2;

// Errors that end the camera session; the rest are reported but leave the preview running.
const FATAL_ERROR_CODES: ReadonlySet<NativeCameraErrorCode> = new Set([
	"permission_denied",
	"unavailable",
	"not_native_mode",
]);

/**
 * Page-side state of the wrapper's camera. One camera exists per page, so this is a singleton:
 * the slot component drives start/layout/stop, the capture pipeline asks it for stills, and
 * React reads it through `useSyncExternalStore`.
 */
class NativeCameraController {
	private snapshot: NativeCameraSnapshot = INITIAL_SNAPSHOT;
	private readonly listeners = new Set<() => void>();
	private readonly barcodeListeners = new Set<(barcode: NativeCameraBarcodePayload) => void>();
	private readonly textListeners = new Set<(text: NativeCameraTextPayload) => void>();
	private unsubscribe: (() => void) | null = null;
	private pendingStart: Deferred<NativeCameraReadyPayload> | null = null;
	private readonly pendingCaptures = new Map<string, Deferred<NativeCameraPhotoPayload>>();
	private captureSequence = 0;
	private lastStart: NativeCameraStartPayload | null = null;

	readonly subscribe = (listener: () => void): (() => void) => {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	};

	readonly getSnapshot = (): NativeCameraSnapshot => this.snapshot;

	onBarcode(listener: (barcode: NativeCameraBarcodePayload) => void): () => void {
		this.barcodeListeners.add(listener);
		return () => {
			this.barcodeListeners.delete(listener);
		};
	}

	onText(listener: (text: NativeCameraTextPayload) => void): () => void {
		this.textListeners.add(listener);
		return () => {
			this.textListeners.delete(listener);
		};
	}

	setSlotActive(slotActive: boolean): void {
		this.update({ slotActive });
	}

	/** Attaches the bridge listener; called at boot, before any camera page mounts. */
	listen(): void {
		this.ensureListening();
	}

	start(payload: NativeCameraStartPayload): Promise<NativeCameraReadyPayload> {
		this.ensureListening();
		this.rejectPendingStart(new NativeCameraError("superseded", "A newer camera.start replaced this one"));
		this.lastStart = payload;
		this.update({ status: "starting", facing: payload.facing, geometry: null, state: null, focus: null, error: null });
		logger.info("INFO", "Native camera start", {
			facing: payload.facing,
			detect: payload.detect,
			slot: payload.slot,
			viewport: pageViewport(),
		});
		const promise = new Promise<NativeCameraReadyPayload>((resolve, reject) => {
			const timer = window.setTimeout(() => {
				this.pendingStart = null;
				this.update({ status: "error", error: { code: "timeout", message: "camera.ready timed out" } });
				reject(new NativeCameraError("timeout", "Native camera did not become ready in time"));
			}, START_TIMEOUT_MS);
			this.pendingStart = { resolve, reject, timer };
		});
		sendOutboundNativeMessage("camera.start", payload);
		return promise;
	}

	/** Re-sends the last `camera.start`; the answer for a Retry tap after `camera.error`. */
	restart(): void {
		if (!this.lastStart) return;
		this.start(this.lastStart).catch((error: unknown) => {
			if (error instanceof NativeCameraError && error.code === "superseded") return;
			logger.warning("INFO", "Native camera failed to restart", describeNativeCameraError(error));
		});
	}

	layout(slot: NativeCameraSlot): void {
		if (this.lastStart) this.lastStart = { ...this.lastStart, slot };
		sendOutboundNativeMessage("camera.layout", { slot });
	}

	configure(settings: NativeCameraConfigurePayload): void {
		sendOutboundNativeMessage("camera.configure", settings);
	}

	/** One-shot focus and exposure at a slot point; the phase is tracked from the wrapper's replies. */
	focus(point: { x: number; y: number }, lock = false): void {
		const payload: NativeCameraFocusPayload = { x: point.x, y: point.y, ...(lock ? { lock } : {}) };
		this.update({ focus: { phase: "adjusting", point } });
		sendOutboundNativeMessage("camera.focus", payload);
	}

	/**
	 * Focuses and resolves once the wrapper reports a settled phase (`focused`, `failed` or
	 * `reset`), or with null when nothing arrives within `timeoutMs`.
	 */
	focusAndSettle(
		point: { x: number; y: number },
		{ lock = false, timeoutMs = FOCUS_SETTLE_TIMEOUT_MS }: { lock?: boolean; timeoutMs?: number } = {},
	): Promise<NativeCameraFocusEventPayload | null> {
		this.focus(point, lock);
		return new Promise((resolve) => {
			let unsubscribe = () => {};
			let timer = 0;
			const finish = (event: NativeCameraFocusEventPayload | null) => {
				unsubscribe();
				window.clearTimeout(timer);
				resolve(event);
			};
			unsubscribe = this.subscribe(() => {
				const focus = this.snapshot.focus;
				if (focus && focus.phase !== "adjusting") finish(focus);
			});
			timer = window.setTimeout(() => finish(null), timeoutMs);
		});
	}

	/** Returns the camera to continuous autofocus, releasing a locked tap focus; a stopped camera has none. */
	releaseFocus(): void {
		if (this.snapshot.status !== "ready") return;
		this.configure({ focusMode: "continuous" });
	}

	capture(options: Omit<NativeCameraCapturePayload, "requestId"> = {}): Promise<NativeCameraPhotoPayload> {
		this.ensureListening();
		const requestId = `capture-${++this.captureSequence}`;
		const promise = new Promise<NativeCameraPhotoPayload>((resolve, reject) => {
			const timer = window.setTimeout(() => {
				this.pendingCaptures.delete(requestId);
				logger.warning("INFO", "Native camera capture timed out", { requestId, timeoutMs: CAPTURE_TIMEOUT_MS });
				reject(new NativeCameraError("timeout", "Native camera did not deliver the photo in time"));
			}, CAPTURE_TIMEOUT_MS);
			this.pendingCaptures.set(requestId, { resolve, reject, timer });
		});
		sendOutboundNativeMessage("camera.capture", { requestId, ...options });
		return promise;
	}

	stop(): void {
		const reason = new NativeCameraError("superseded", "camera.stop was sent");
		this.rejectPendingStart(reason);
		this.rejectPendingCaptures(reason);
		this.lastStart = null;
		sendOutboundNativeMessage("camera.stop", {});
		this.update({ status: "stopped", focus: null });
	}

	private ensureListening(): void {
		if (this.unsubscribe) return;
		this.unsubscribe = addInboundNativeMessageListener((message) => this.handleMessage(message));
	}

	// Lifecycle events each leave a log row, so a silent bridge can be told apart from a silent
	// wrapper; reads and quality hints arrive too often to log.
	private handleMessage(message: InboundNativeMessage): void {
		switch (message.message) {
			case "native.ready":
				this.handleBridgeReady(message.payload, message.message);
				return;
			case "pong":
				// On devices native.ready itself did not reach the page, so the boot ping's answer
				// (the same payload from build 18) is the dependable source.
				if (typeof message.payload === "object") {
					this.handleBridgeReady(message.payload, message.message);
					return;
				}
				logger.info("INFO", "Native event received", { message: message.message });
				this.update({ lastEvent: message.message });
				return;
			case "camera.ready":
				this.handleReady(message.payload);
				return;
			case "camera.geometry":
				if (this.snapshot.geometry === null) logger.info("INFO", "Native camera geometry", message.payload);
				this.warnOnDisplacedSlot(message.payload);
				this.update({ geometry: message.payload, lastEvent: message.message });
				return;
			case "camera.state": {
				// A zoom ramp reports dozens of states; only a change of torch or mode is worth a row.
				const previous = this.snapshot.state;
				const next = message.payload;
				if (
					!previous ||
					previous.torch !== next.torch ||
					previous.focusMode !== next.focusMode ||
					previous.exposureMode !== next.exposureMode
				) {
					logger.info("INFO", "Native camera state", next);
				}
				this.update({ state: next, lastEvent: message.message });
				return;
			}
			case "camera.focus":
				logger.info("INFO", "Native camera focus", {
					phase: message.payload.phase,
					focus: message.payload.focus,
					exposure: message.payload.exposure,
					locked: message.payload.locked,
					message: message.payload.message,
				});
				this.update({ focus: message.payload, lastEvent: message.message });
				return;
			// The wrapper streams reads many times a second; the IMEI screen logs the one it acts on.
			case "camera.barcode":
				this.noteEvent(message.message);
				for (const listener of this.barcodeListeners) listener(message.payload);
				return;
			case "camera.text":
				this.noteEvent(message.message);
				for (const listener of this.textListeners) listener(message.payload);
				return;
			case "camera.quality":
				this.noteEvent(message.message);
				return;
			case "device.thermal":
				logger.info("INFO", "Native device thermal", message.payload);
				this.update({ thermal: message.payload.state, lastEvent: message.message });
				return;
			case "camera.photo":
				this.handlePhoto(message.payload);
				return;
			case "camera.stopped":
				this.handleStopped(message.payload);
				return;
			case "camera.error":
				this.handleError(message.payload);
				return;
			default:
				logger.info("INFO", "Native event received", { message: message.message });
				this.update({ lastEvent: message.message });
		}
	}

	private handleBridgeReady(payload: NativeReadyPayload, via: "native.ready" | "pong"): void {
		// The only place the wrapper names the hardware; pinned as a common prop so every row of
		// the session is attributable to a device model.
		const deviceModel =
			typeof payload.deviceModel === "string" && payload.deviceModel.length > 0 ? payload.deviceModel : null;
		if (deviceModel) logger.injectCommonProp("pandas_device_model", deviceModel);
		logger.info("INFO", "Native bridge ready", {
			version: payload.version,
			capabilities: payload.capabilities,
			cameraMode: payload.cameraMode,
			thermal: payload.thermal,
			deviceModel: payload.deviceModel,
			minimumFocusDistanceMillimeters: payload.minimumFocusDistanceMillimeters,
			via,
		});
		this.update({
			thermal: payload.thermal ?? this.snapshot.thermal,
			deviceModel: deviceModel ?? this.snapshot.deviceModel,
			lastEvent: via,
		});
	}

	private handleReady(payload: NativeCameraReadyPayload): void {
		logger.info("INFO", "Native camera ready", {
			facing: payload.facing,
			width: payload.width,
			height: payload.height,
			fps: payload.fps,
			deviceModel: payload.deviceModel,
			minimumFocusDistanceMillimeters: payload.minimumFocusDistanceMillimeters,
			capabilities: payload.capabilities,
		});
		this.update({
			status: "ready",
			facing: payload.facing,
			capabilities: payload.capabilities,
			error: null,
			lastEvent: "camera.ready",
		});
		const pending = this.pendingStart;
		this.pendingStart = null;
		if (pending) {
			window.clearTimeout(pending.timer);
			pending.resolve(payload);
		}
	}

	/**
	 * The wrapper echoes the slot it actually laid out (build 7); it may differ from the sent slot
	 * only by screen clamping. Anything past layout rounding means the preview sits somewhere the
	 * page did not put it — the failure that cut photos, surfaced as a row instead of as stills.
	 */
	private warnOnDisplacedSlot(geometry: NativeCameraGeometryPayload): void {
		const applied = geometry.appliedSlot;
		const sent = this.lastStart?.slot;
		if (!applied || !sent) return;
		const offBy = Math.max(
			Math.abs(applied.x - sent.x),
			Math.abs(applied.y - sent.y),
			Math.abs(applied.width - sent.width),
			Math.abs(applied.height - sent.height),
		);
		// A malformed echo compares as NaN; a diagnostic must never add noise or stop the camera.
		if (!(offBy > SLOT_ECHO_TOLERANCE_PX)) return;
		logger.warning("INFO", "Native preview sits off the slot the page sent", {
			sent,
			applied,
			offBy: Math.round(offBy * 100) / 100,
			viewport: pageViewport(),
		});
	}

	private handlePhoto(payload: NativeCameraPhotoPayload): void {
		// Payloads are not validated on arrival; a photo without image data fails its capture now
		// instead of leaving it to the timeout.
		if (typeof payload.jpegBase64 !== "string" || payload.jpegBase64.length === 0) {
			this.handleError({
				requestId: payload.requestId,
				code: "capture_failed",
				message: "camera.photo carried no image data",
			});
			return;
		}
		logger.info("INFO", "Native camera photo", {
			requestId: payload.requestId,
			width: payload.width,
			height: payload.height,
			sourceWidth: payload.sourceWidth,
			sourceHeight: payload.sourceHeight,
			base64Length: payload.jpegBase64.length,
			cropped: payload.cropped ?? false,
			metrics: payload.metrics,
		});
		this.update({ lastEvent: "camera.photo" });
		const requestId = payload.requestId ?? this.pendingCaptures.keys().next().value;
		const pending = requestId ? this.pendingCaptures.get(requestId) : undefined;
		if (!requestId || !pending) {
			logger.warning("INFO", "Native camera photo without a pending capture", { requestId: payload.requestId });
			return;
		}
		this.pendingCaptures.delete(requestId);
		window.clearTimeout(pending.timer);
		pending.resolve(payload);
	}

	private handleStopped(payload: NativeCameraStoppedPayload): void {
		logger.info("INFO", "Native camera stopped", payload);
		switch (payload.reason) {
			case "interrupted":
				// The wrapper resumes on its own and sends camera.ready again.
				this.update({ status: "interrupted", lastEvent: "camera.stopped" });
				return;
			case "reconfigure":
				this.update({ status: "starting", lastEvent: "camera.stopped" });
				return;
			case "page":
				// A stop sent before a newer camera.start can be echoed after it, while that start is pending.
				if (this.pendingStart) {
					this.update({ lastEvent: "camera.stopped" });
					return;
				}
				this.update({ status: "stopped", lastEvent: "camera.stopped" });
				return;
			default: {
				const failure: NativeCameraFailure = { code: "unavailable", message: payload.detail ?? payload.reason };
				this.update({ status: "error", error: failure, lastEvent: "camera.stopped" });
				this.rejectPendingStart(new NativeCameraError(failure.code, failure.message));
				this.rejectPendingCaptures(new NativeCameraError(failure.code, failure.message));
			}
		}
	}

	private handleError(payload: NativeCameraErrorPayload): void {
		logger.warning("INFO", "Native camera error", payload);
		const error = new NativeCameraError(payload.code, payload.message);
		const pendingCapture = payload.requestId ? this.pendingCaptures.get(payload.requestId) : undefined;
		if (payload.requestId && pendingCapture) {
			this.pendingCaptures.delete(payload.requestId);
			window.clearTimeout(pendingCapture.timer);
			pendingCapture.reject(error);
			this.update({ lastEvent: "camera.error" });
			return;
		}
		if (payload.code === "capture_failed") {
			// Without an id the failure cannot be matched, so every pending capture fails; an id
			// that matches none belongs to a capture that already ended.
			if (!payload.requestId) this.rejectPendingCaptures(error);
			this.update({ lastEvent: "camera.error" });
			return;
		}
		const failure: NativeCameraFailure = { code: payload.code, message: payload.message };
		if (FATAL_ERROR_CODES.has(payload.code)) {
			this.update({ status: "error", error: failure, lastEvent: "camera.error" });
			this.rejectPendingStart(error);
			this.rejectPendingCaptures(error);
			return;
		}
		this.update({ error: failure, lastEvent: "camera.error" });
	}

	private rejectPendingStart(error: NativeCameraError): void {
		const pending = this.pendingStart;
		if (!pending) return;
		this.pendingStart = null;
		window.clearTimeout(pending.timer);
		pending.reject(error);
	}

	private rejectPendingCaptures(error: NativeCameraError): void {
		for (const [requestId, pending] of this.pendingCaptures) {
			this.pendingCaptures.delete(requestId);
			window.clearTimeout(pending.timer);
			pending.reject(error);
		}
	}

	// Reads repeat the same event name many times a second; subscribers only re-render on a change.
	private noteEvent(lastEvent: string): void {
		if (this.snapshot.lastEvent !== lastEvent) this.update({ lastEvent });
	}

	private update(partial: Partial<NativeCameraSnapshot>): void {
		this.snapshot = { ...this.snapshot, ...partial };
		for (const listener of this.listeners) listener();
	}
}

export const nativeCamera = new NativeCameraController();
